import { ImgID } from "../../image_loader/img_id.js";
import { ColorScheme } from "../common_ui_elements/color_scheme.js";
import { TextStyleScheme } from "../common_ui_elements/text_style_scheme.js";
import { UIImage } from "../common_ui_elements/ui_image.js";
import { UINineSliceElement } from "../common_ui_elements/ui_nine_slice_element.js";
import { MyTextAlign, MyTextVerticalAlign, UITextElement } from "../common_ui_elements/ui_text_element.js";
import { AbstractDialog } from "./abstract_dialog.js";
import { DialogId } from "./dialog_id.js";
import { DialogManager } from "./dialog_manager.js";

export interface NoLevelsDialogParams
{
  onClose?: () => void;
}

export class NoLevelsDialog extends AbstractDialog
{
  public static readonly dialog_id = DialogId.NO_LEVELS_DIALOG;

  private static readonly DESIGN_W = 1080;
  private static readonly DESIGN_H = 1335;
  private callback_called = false;

  public constructor( private readonly params: NoLevelsDialogParams = {} )
  {
    const body_w = LayoutScaler.widthFromDesign( NoLevelsDialog.DESIGN_W );
    const body_h = LayoutScaler.heightFromDesign( NoLevelsDialog.DESIGN_H );
    const body_x = ( DialogManager.getWidth() - body_w ) / 2;
    const body_y = ( DialogManager.getHeight() - body_h ) / 2;

    super( DialogManager.ctx, body_x, body_y, body_w, body_h, false );

    this.createPanel();
    this.createTitleRibbon();
    this.createTiger();
    this.createTitle();
    this.createCopy();
    this.createButton();
  }

  private createPanel(): void
  {
    const panel_x = LayoutScaler.xFromDesign( 85.58 );
    const panel_y = LayoutScaler.yFromDesign( 445.54 );
    const panel_w = LayoutScaler.widthFromDesign( 909.42 );
    const panel_h = LayoutScaler.heightFromDesign( 856 );
    this.addChild( new UINineSliceElement(
      this.ctx,
      panel_x,
      panel_y,
      panel_w,
      panel_h,
      ImgID.UI_CapsuleBlue,
      "capsule_bg"
    ) );

    this.addChild( new UIImage(
      this.ctx,
      LayoutScaler.xFromDesign( 86 ),
      LayoutScaler.yFromDesign( 470.54 ),
      LayoutScaler.widthFromDesign( 909 ),
      LayoutScaler.heightFromDesign( 207 ),
      ImgID.UI_NoLevelsGradient
    ) );
  }

  private createTiger(): void
  {
    const tiger_w = LayoutScaler.widthFromDesign( 417.05 );
    const tiger_h = LayoutScaler.heightFromDesign( 412.03 );
    this.addChild( new UIImage(
      this.ctx,
      LayoutScaler.xFromDesign( 312.58 ),
      LayoutScaler.yFromDesign( 3.5 ),
      tiger_w,
      tiger_h,
      ImgID.UI_NoLevelsTiger
    ) );
  }

  private createTitleRibbon(): void
  {
    this.addChild( new UIImage(
      this.ctx,
      LayoutScaler.xFromDesign( 18.58 ),
      LayoutScaler.yFromDesign( 316 ),
      LayoutScaler.widthFromDesign( 1042.83 ),
      LayoutScaler.heightFromDesign( 323.54 ),
      ImgID.UI_NoLevelsRibbon
    ) );
  }

  private createTitle(): void
  {
    this.addChild( new UITextElement(
      this.ctx,
      LayoutScaler.xFromDesign( 346.58 ),
      LayoutScaler.yFromDesign( 385.53 ),
      LayoutScaler.widthFromDesign( 386 ),
      LayoutScaler.heightFromDesign( 98 ),
      {
        localization_key: "EXCELLENT",
        font_size: LayoutScaler.textSizeFromDesign( 70 ),
        min_font_size: LayoutScaler.textSizeFromDesign( 48 ),
        font_weight: 900,
        text_color: ColorScheme.white,
        text_style: TextStyleScheme.rubik,
        text_align: MyTextAlign.Center,
        vertical_align: MyTextVerticalAlign.Center,
        auto_wrap: false,
        fit_font_size: true,
        outline_width: LayoutScaler.textBorderSizeFromDesign( 14 ),
        outline_color: "#003F87",
        ...TextStyleScheme.buttonShadow(),
      }
    ) );
  }

  private createCopy(): void
  {
    this.addChild( new UITextElement(
      this.ctx,
      LayoutScaler.xFromDesign( 261.58 ),
      LayoutScaler.yFromDesign( 585.53 ),
      LayoutScaler.widthFromDesign( 556 ),
      LayoutScaler.heightFromDesign( 168 ),
      {
        localization_key: "NO_LEVELS_DIALOG.FIRST_LEVELS",
        font_size: Math.min(
          LayoutScaler.textSizeFromDesign( 70 ),
          LayoutScaler.widthFromDesign( 70 )
        ),
        min_font_size: Math.min(
          LayoutScaler.textSizeFromDesign( 50 ),
          LayoutScaler.widthFromDesign( 50 )
        ),
        font_weight: 900,
        text_color: ColorScheme.white,
        text_style: TextStyleScheme.rubik,
        text_align: MyTextAlign.Center,
        vertical_align: MyTextVerticalAlign.Center,
        auto_wrap: true,
        fit_font_size: true,
        line_height: 0.95,
        outline_width: LayoutScaler.textBorderSizeFromDesign( 14 ),
        outline_color: "#003F87",
      }
    ) );

    this.addChild( new UIImage(
      this.ctx,
      LayoutScaler.xFromDesign( 304.58 ),
      LayoutScaler.yFromDesign( 766.29 ),
      LayoutScaler.widthFromDesign( 470 ),
      LayoutScaler.heightFromDesign( 49.5 ),
      ImgID.UI_NoLevelsTape
    ) );

    this.addChild( new UITextElement(
      this.ctx,
      LayoutScaler.xFromDesign( 148.58 ),
      LayoutScaler.yFromDesign( 822.53 ),
      LayoutScaler.widthFromDesign( 783 ),
      LayoutScaler.heightFromDesign( 195 ),
      {
        localization_key: "NO_LEVELS_DIALOG.COMING_SOON",
        font_size: Math.min(
          LayoutScaler.textSizeFromDesign( 70 ),
          LayoutScaler.widthFromDesign( 70 )
        ),
        min_font_size: Math.min(
          LayoutScaler.textSizeFromDesign( 50 ),
          LayoutScaler.widthFromDesign( 50 )
        ),
        font_weight: 900,
        text_color: ColorScheme.white,
        text_style: TextStyleScheme.rubik,
        text_align: MyTextAlign.Center,
        vertical_align: MyTextVerticalAlign.Center,
        auto_wrap: true,
        fit_font_size: true,
        line_height: 0.95,
        outline_width: LayoutScaler.textBorderSizeFromDesign( 14 ),
        outline_color: "#003F87",
      }
    ) );
  }

  private createButton(): void
  {
    const button_w = LayoutScaler.widthFromDesign( 700 );
    const button_h = LayoutScaler.heightFromDesign( 280 );
    const button = new UIImage(
      this.ctx,
      ( this.rect.getW() - button_w ) / 2,
      LayoutScaler.yFromDesign( 979.54 ),
      button_w,
      button_h,
      ImgID.UI_GreenFrameBtn,
      undefined,
      true
    );

    button.addChild( new UITextElement(
      this.ctx,
      0,
      LayoutScaler.yFromDesign( 5 ),
      button_w,
      button_h,
      {
        localization_key: "OK",
        font_size: LayoutScaler.textSizeFromDesign( 68 ),
        font_weight: 900,
        text_color: ColorScheme.white,
        text_style: TextStyleScheme.rubik,
        text_align: MyTextAlign.Center,
        vertical_align: MyTextVerticalAlign.Center,
        outline_color: ColorScheme.menu_button_outline,
        outline_width: LayoutScaler.textBorderSizeFromDesign( 10 ),
        ...TextStyleScheme.buttonShadow(),
      }
    ), true );

    button.setOnClick( () => DialogManager.closeDialog( "no_levels_ok" ) );
    this.addChild( button );
  }

  public override onDialogWillClose(): void
  {
    if ( this.callback_called )
      return;

    this.callback_called = true;
    this.params.onClose?.();
  }

  public render(): void
  {
    if ( !this.shouldBeRendered() )
      return;

    this.startRender();
    this.finishRender();
  }

  public name(): string
  {
    return "NoLevelsDialog";
  }
}
